<template>
    <transition name="nav-fade" appear>
        <v-container
            v-if="flags.isOpen"
            fluid
            class="snb overflower"
            ref="scrollContainer"
            v-on:scroll="handleScroll"
            v-on:mouseenter="handleMouseEnter"
            v-on:mouseleave="handleMouseLeave"
        >
            <v-container class="snb__inner">
                <!-- 대분류 탭 -->
                <div class="snb__tabs">
                    <button
                        v-for="(sitemap, index) in sitemaps"
                        v-bind:key="index"
                        v-on:click="setActiveSection(index)"
                        class="snb__tab-btn"
                        v-bind:class="{ 'is-active': states.activeSectionIndex === index }"
                    >
                        <span>{{ sitemap.SECTION.NAME }}</span>
                    </button>
                    <button class="snb__tab-btn snb__search-btn" v-bind:class="{ 'is-active': flags.isSearching }" v-on:click="toggleSearch">
                        <v-icon v-bind:size="20">{{ flags.isSearching ? 'mdi-close' : 'mdi-magnify' }}</v-icon>
                    </button>
                    <transition name="search-slide">
                        <input
                            v-if="flags.isSearching"
                            ref="searchInput"
                            v-model="states.searchQuery"
                            type="text"
                            class="snb__search-input"
                            placeholder="..."
                            v-on:focus="flags.isSearchFocused = true"
                            v-on:blur="flags.isSearchFocused = false"
                        />
                    </transition>
                </div>

                <!-- 상세메뉴 영역 -->
                <v-row>
                    <v-col cols="12" class="snb__content" v-if="activeSitemap">
                        <div class="snb__groups" v-if="filteredGroups.length">
                            <div v-for="(groupData, gIndex) in filteredGroups" v-bind:key="gIndex" class="snb__group">
                                <h3 class="snb__group-title">
                                    <span>{{ groupData.GROUP.SHORTNAME || groupData.GROUP.NAME }}</span>
                                </h3>
                                <div class="snb__group-tabs">
                                    <div v-for="(tab, tIndex) in groupData.TABS" v-bind:key="tIndex" class="snb__tab">
                                        <h4 class="snb__tab-title" v-if="tab.TYPE === 'tab'">
                                            <span>{{ tab.NAME }}</span>
                                        </h4>
                                        <ul class="snb__items">
                                            <li v-for="(item, iIndex) in tab.ITEMS" v-bind:key="iIndex" class="snb__item">
                                                <router-link v-bind:to="item.LINK">{{ item.NAME }}</router-link>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else class="snb__search-empty">
                            <span>검색 결과가 없어요</span>
                        </div>
                    </v-col>
                </v-row>

                <div class="snb__scroll-anchor">
                    <transition name="fade">
                        <button class="snb__scroll-btn" v-show="flags.canScroll && !flags.isScrolledToBottom" v-on:click="scrollToBottom">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z" />
                            </svg>
                        </button>
                    </transition>
                </div>
            </v-container>
        </v-container>
    </transition>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { getVisibleSitemaps } from './menus/index.js';

    export default {
        name: 'ServiceNavs',
        props: {
            show: { type: Boolean, required: true, default: false },
        },
        data() {
            return {
                flags: {
                    isOpen: this.show,
                    isLoading: false,
                    canScroll: false,
                    isScrolledToBottom: false,
                    isSearching: false,
                    isSearchFocused: false,
                },
                states: {
                    activeSectionIndex: 0,
                    searchQuery: '',
                },
            };
        },
        watch: {
            show(val) {
                this.flags.isOpen = val;
                if (val) {
                    this.states.activeSectionIndex = 0;
                    this.closeSearch();
                    this.$nextTick(() => {
                        this.checkScrollable();
                    });
                } else {
                    this.removeResizeListener();
                }
            },
            'flags.isOpen'(val) {
                if (!val) {
                    this.$emit('close');
                }
            },
            'states.activeSectionIndex'() {
                if (this.flags.isOpen) {
                    this.$nextTick(() => {
                        this.checkScrollable();
                    });
                }
            },
            $route() {
                this.flags.isOpen = false;
            },
        },
        mounted() {
            if (this.flags.isOpen) {
                this.$nextTick(() => {
                    this.checkScrollable();
                });
            }
        },
        beforeDestroy() {
            this.removeResizeListener();
        },
        computed: {
            ...mapGetters('app', ['demoMode']),
            ...mapGetters('auth', ['shopTheme']),
            sitemaps() {
                return getVisibleSitemaps(this.shopTheme);
            },
            activeSitemap() {
                return this.sitemaps[this.states.activeSectionIndex] || null;
            },
            filteredGroups() {
                if (!this.activeSitemap) return [];
                const query = this.states.searchQuery.trim().toLowerCase();
                if (!query) return this.activeSitemap.GROUPS;

                return this.activeSitemap.GROUPS.map((groupData) => {
                    const filteredTabs = groupData.TABS.map((tab) => ({
                        ...tab,
                        ITEMS: tab.ITEMS.filter(
                            (item) => item.NAME.toLowerCase().includes(query) || (item.KEYWORDS || []).some((kw) => kw.toLowerCase().includes(query)),
                        ),
                    })).filter((tab) => tab.ITEMS.length > 0);

                    if (!filteredTabs.length) return null;
                    return { ...groupData, TABS: filteredTabs };
                }).filter(Boolean);
            },
        },
        methods: {
            setActiveSection(index) {
                this.states.activeSectionIndex = index;
            },
            toggleSearch() {
                if (this.flags.isSearching) {
                    this.closeSearch();
                } else {
                    this.flags.isSearching = true;
                    this.$nextTick(() => {
                        this.$refs.searchInput?.focus();
                    });
                }
            },
            closeSearch() {
                this.flags.isSearching = false;
                this.states.searchQuery = '';
            },
            handleMouseEnter() {
                window.clearTimeout(this._closeTimeout);
            },
            handleMouseLeave() {
                if (this.flags.isSearchFocused) return;
                window.clearTimeout(this._closeTimeout);
                this._closeTimeout = window.setTimeout(() => {
                    this.flags.isOpen = false;
                }, 400);
            },
            handleScroll(e) {
                const target = e.target;
                if (!target) return;
                this.flags.isScrolledToBottom = target.scrollHeight - target.scrollTop - target.clientHeight <= 2;
            },
            checkScrollable() {
                const el = this.$refs.scrollContainer?.$el || this.$refs.scrollContainer;
                if (!el) return;

                this.flags.canScroll = el.scrollHeight > el.clientHeight;
                this.flags.isScrolledToBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 2;
                this.addResizeListener();
            },
            addResizeListener() {
                if (!this._resizeListenerAdded) {
                    window.addEventListener('resize', this.checkScrollable);
                    this._resizeListenerAdded = true;
                }
            },
            removeResizeListener() {
                if (this._resizeListenerAdded) {
                    window.removeEventListener('resize', this.checkScrollable);
                    this._resizeListenerAdded = false;
                }
            },
            scrollToBottom() {
                const el = this.$refs.scrollContainer?.$el || this.$refs.scrollContainer;
                if (el) {
                    el.scrollTo({
                        top: el.scrollHeight,
                        behavior: 'smooth',
                    });
                }
            },
        },
    };
</script>
